import React, { useState, useEffect } from "react";
import { Modal, Button, message, Flex, Input, Typography } from "antd";
import { useSelector, useDispatch } from "react-redux";
import { useIntl } from "react-intl";
import { login_with_token } from "../../../api/server";
import Auth from "../../../api/auth";
import { setEmailFactorModalVisible } from "../../../features/user/UserSlice";

const { Text } = Typography;

const EmailFactorModal = () => {
  const [otpCode, setOtpCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [timer, setTimer] = useState(300); // 5 minutes in seconds
  const dispatch = useDispatch();
  const intl = useIntl();
  const user = useSelector((state) => state.user.firebaseUser);
  const visible = useSelector((state) => state.user.emailFactorModalVisible);

  useEffect(() => {
    let interval;
    if (visible) {
      setTimer(300); // Reset timer when modal becomes visible
      interval = setInterval(() => {
        setTimer((prevTimer) => {
          if (prevTimer <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prevTimer - 1;
        });
      }, 1000);
    }
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [visible]);

  const handleOtpChange = (value) => {
    setOtpCode(value);
  };

  const handleSubmit = async () => {
    if (otpCode.length !== 6) {
      message.error(
        intl.formatMessage({ id: "app.emailFactor.invalidCodeLength" })
      );
      return;
    }

    setLoading(true);
    try {
      await Auth.loginWithUserToken({ user, code: otpCode });
    } catch (error) {
      console.error("Error during email factor authentication:", error);
      message.error(intl.formatMessage({ id: "app.emailFactor.error" }));
    } finally {
      setLoading(false);
      setOtpCode("");
    }
  };

  const handleResend = async () => {
    setLoading(true);
    try {
      const token = await user?.getIdToken();
      await login_with_token({ token });
      setOtpCode("");
      message.success(intl.formatMessage({ id: "app.emailFactor.codeSent" }));
      setTimer(300); // Reset timer to 5 minutes
    } catch (error) {
      console.error("Error resending code:", error);
      message.error(intl.formatMessage({ id: "app.emailFactor.resendError" }));
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  return (
    <Modal
      title={intl.formatMessage({ id: "app.emailFactor.modalTitle" })}
      open={visible}
      onCancel={() => {
        dispatch(setEmailFactorModalVisible(false));
        setOtpCode(""); // Reset OTP code when modal is closed
        Auth.signOut();
      }}
      footer={null}
    >
      <Flex gap="large" align="flex-start" vertical>
        <Text>{intl.formatMessage({ id: "app.emailFactor.enterCode" })}</Text>
        <Flex justify="center" style={{ margin: "24px 0", width: "100%" }}>
          <Input.OTP
            length={6}
            onChange={handleOtpChange}
            autoFocus
            inputType="numeric"
            style={{ direction: "ltr" }}
            value={otpCode}
          />
        </Flex>
        <Flex justify="space-between" align="center" style={{ width: "100%" }}>
          <Button
            type="primary"
            onClick={handleSubmit}
            loading={loading}
            disabled={timer === 0}
          >
            {intl.formatMessage({ id: "app.emailFactor.submit" })}
          </Button>
          <Flex align="center">
            <Text style={{ marginRight: "8px" }}>
              {timer > 0
                ? formatTime(timer)
                : intl.formatMessage({ id: "app.emailFactor.timeUp" })}
            </Text>
            <Button
              type="text"
              onClick={handleResend}
              loading={loading}
              //   disabled={timer > 0}
            >
              {intl.formatMessage({ id: "app.emailFactor.resendCode" })}
            </Button>
          </Flex>
        </Flex>
      </Flex>
    </Modal>
  );
};

export default EmailFactorModal;
